import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { PortalModal } from "@/components/ui/portal-modal";
import { cn } from "@/lib/utils";
import { Lock, Eye, EyeOff } from "lucide-react";

interface DevAuthModalProps {
  open: boolean;
  isVerifying: boolean;
  onSubmit: (password: string) => Promise<void>;
}

export const DevAuthModal = ({ open, isVerifying, onSubmit }: DevAuthModalProps) => {
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [showPassword, setShowPassword] = useState(false);

  const handleTogglePassword = () => setShowPassword((show) => !show);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    try {
      await onSubmit(password);
    } catch {
      setError("Invalid development password");
      setPassword("");
    }
  };

  return (
    <PortalModal open={open} onOpenChange={() => null} disableBackdropClick className="max-w-md">
      <form onSubmit={handleSubmit} className="flex flex-col gap-6 w-full focus:outline-none">
        <PortalModal.Header>
          <div className="flex items-center gap-2">
            <Lock className="h-5 w-5 text-cede-primary" />
            <PortalModal.Title>Development Environment</PortalModal.Title>
          </div>
        </PortalModal.Header>

        <PortalModal.Content className="flex flex-col gap-6">
          <p className="text-body2 text-cede-text-secondary">
            This is a development environment. Please enter the development password to continue.
          </p>

          <div className="flex flex-col gap-2">
            <Label htmlFor="dev-password-input" className={cn(error && "text-cede-error")}>
              Development Password
            </Label>
            <div className="relative">
              <Input
                id="dev-password-input"
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                disabled={isVerifying}
                className={cn("pr-10", error && "border-cede-error")}
                placeholder="Enter password"
              />
              <button
                type="button"
                onClick={handleTogglePassword}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-cede-text-secondary hover:text-cede-text-primary"
                aria-label="Toggle password visibility"
              >
                {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
            {error && <p className="text-sm text-cede-error">{error}</p>}
          </div>

          <Button type="submit" variant="primary" loading={isVerifying} disabled={!password} className="w-full">
            Verify
          </Button>
        </PortalModal.Content>
      </form>
    </PortalModal>
  );
};
